import React, { useMemo, useState } from 'react';

export default function SavedRaidsPanel({
  authUser,
  savedRaids,
  selectedIds,
  onSelect, // 배열(nextIds)
  onCloudLoad,
  onUploadSelectedToCloud,   // ⬅️ 토글 버튼에서 사용
  onDeleteFromCloudOnly,
  onLoadRaid,
  onDeleteRaid,
  onCompare,
  canCompare,
  onAverage,
  canAverage,
  onSaveSelectedToLocal,     // ⬅️ 선택 항목을 로컬에 저장
}) {
  const [query, setQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all' | 'cloud' | 'local';

  function formatYMD(ts) {
    const s = String(ts || '');
    const m = s.replace(/[^\d]/g, ' ').trim().split(/\s+/);
    if (m.length < 3) return '';
    const [y, mo, da] = m;
    const z2 = v => String(v).padStart(2, '0');
    return `${y}-${z2(mo)}-${z2(da)}`;
  }

  // 리스트 항목 + 상태 뱃지
  const items = useMemo(() => {
    return savedRaids.map(r => {
      const ymd = formatYMD(r.timestamp);
      const hasLocal = (r._local ?? (r.source === 'local')) === true;
      const hasCloud = (r._cloud ?? (r.source === 'cloud')) === true;
      const badges = [
        hasCloud ? '클라우드에 저장됨' : null,
        hasLocal ? '로컬에 저장됨' : null,
      ].filter(Boolean);
      const label =
        `${r.job || '직업?'}${r.jobEngraving ? `(${r.jobEngraving})` : ''} / ` +
        `${r.raidName}${ymd ? ` (${ymd})` : ''} / ${r.battleTime || ''}` +
        `${r.memo ? ` / ${r.memo}` : ''}`;
      return { id: r.id, label, raw: r, badges, hasLocal, hasCloud };
    });
  }, [savedRaids]);

  // 검색 필터
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let arr = items;
    if (sourceFilter === 'cloud') arr = arr.filter(it => it.hasCloud);
    else if (sourceFilter === 'local') arr = arr.filter(it => it.hasLocal);
    if (q) arr = arr.filter(it => it.label.toLowerCase().includes(q));
    return arr;
  }, [items, query, sourceFilter]);

  // 선택 헬퍼
  const emitSelect = (nextIds) => onSelect?.(nextIds);
  const toggle = (id) => {
    const next = selectedIds.includes(id)
      ? selectedIds.filter(x => x !== id)
      : [...selectedIds, id];
    emitSelect(next);
  };
  const clearAll = () => emitSelect([]);

  // 선택된 레코드들
  const selectedRecords = useMemo(
    () => selectedIds.map(id => savedRaids.find(r => r.id === id)).filter(Boolean),
    [selectedIds, savedRaids]
  );

  // 로컬/클라우드 상태 집계
  const selCount = selectedRecords.length;

  const allLocal = selCount > 0 && selectedRecords.every(r => (r._local ?? (r.source === 'local')) === true);
  const anyLocal = selCount > 0 && selectedRecords.some(r => (r._local ?? (r.source === 'local')) === true);
  const noneLocal = selCount > 0 && !anyLocal;
  const mixedLocal = selCount > 0 && !allLocal && !noneLocal;

  const allCloud = selCount > 0 && selectedRecords.every(r => (r._cloud ?? (r.source === 'cloud')) === true);
  const anyCloud = selCount > 0 && selectedRecords.some(r => (r._cloud ?? (r.source === 'cloud')) === true);
  const noneCloud = selCount > 0 && !anyCloud;
  const mixedCloud = selCount > 0 && !allCloud && !noneCloud;

  // ⬇️ 클라우드 버튼(로그인 필요): 저장/삭제 토글
  const cloudBtnLabel =
    noneCloud ? '클라우드에 저장' : '클라우드에서 삭제';
  const cloudBtnOnClick =
    noneCloud ? onUploadSelectedToCloud : onDeleteFromCloudOnly;
  const cloudBtnDisabled =
    !authUser?.uid ||
    selCount < 1 ||
    (noneCloud && !onUploadSelectedToCloud) ||
    mixedCloud;
  const cloudBtnTitle = !authUser?.uid
    ? '로그인이 필요합니다'
    : selCount < 1
      ? '항목을 선택하세요'
      : mixedCloud
        ? '클라우드에 있는 항목만 삭제하거나, 클라우드에 없는 항목만 저장할 수 있어요'
        : cloudBtnLabel;

  // ⬇️ 로컬 버튼(로그인 불필요): 저장/삭제 토글
  const localBtnLabel =
    noneLocal ? '로컬에 저장' : '로컬에서 삭제';
  const localBtnDisabled =
    selCount < 1 ||
    (noneLocal && !onSaveSelectedToLocal) ||
    mixedLocal;
  const localBtnTitle = selCount < 1
    ? '항목을 선택하세요'
    : mixedLocal
      ? '로컬에 있는 항목만 삭제하거나, 로컬에 없는 항목만 저장할 수 있어요'
      : localBtnLabel;

  // ✅ 로컬 토글 클릭 시 alert 처리
  const handleLocalToggle = async () => {
    const count = selCount;
    if (count < 1) return;

    try {
      if (noneLocal) {
        await Promise.resolve(onSaveSelectedToLocal?.());
        alert(`${count}건을 로컬에 저장했습니다.`);
      } else {
        await Promise.resolve(onDeleteRaid?.());
        alert(`로컬에서 ${count}건 삭제했습니다.`);
      }
    } catch (e) {
      alert(`로컬 ${noneLocal ? '저장' : '삭제'} 실패: ${e?.message || e}`);
    }
  };

  return (
    <div className="basis-2/5 flex flex-col gap-2 flex-1 min-h-0">
      {/* 상단 버튼 영역 */}
      <div className="flex flex-wrap gap-2">
        {authUser && (
          <>
            <button
              onClick={() => {
                onCloudLoad?.(); // 원래 클라우드 불러오기 로직
                // 혹시 로컬 상태도 새로고침하고 싶으면 여기서 query 초기화 등도 가능
                setQuery('');
                setSourceFilter('all');
              }}
              className="btn btn-sm btn-primary"
            >
              새로고침
            </button>
            <button
              onClick={cloudBtnOnClick}
              disabled={cloudBtnDisabled}
              className={`btn btn-sm ${noneCloud ? '' : 'btn-danger'}`}
              title={cloudBtnTitle}
            >
              {cloudBtnLabel}
            </button>
          </>
        )}

        {/* ✅ 로컬 토글 버튼은 항상 노출 (로그인 불필요) */}
        <button
          onClick={handleLocalToggle}
          disabled={localBtnDisabled}
          className={`btn btn-sm ${noneLocal ? '' : 'btn-danger'}`}
          title={localBtnTitle}
        >
          {localBtnLabel}
        </button>
        {authUser && (
          <select
            aria-label="저장 위치 필터"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className={`
    w-full sm:w-auto
    sm:pl-2 sm:py-1 sm:pr-9
    text-sm text-zinc-900 dark:text-zinc-100
    focus:outline-none bg-transparent
    ml-0 sm:ml-auto
    [&>option]:bg-white [&>option]:text-zinc-900
    dark:[&>option]:bg-zinc-800 dark:[&>option]:text-zinc-100

    /* 모바일 전용 스타일 */
    appearance-none inline-block rounded-lg border border-zinc-950/10
    cursor-pointer px-4 py-2 pr-8 bg-white font-semibold text-zinc-950
    transition-all focus:ring-1 focus:ring-indigo-500
    disabled:bg-zinc-100 disabled:text-zinc-300 disabled:cursor-not-allowed
    bg-no-repeat bg-[length:16px_16px] bg-[right_8px_center]
    dark:bg-zinc-900 dark:border-white/10 dark:focus:ring-indigo-400
    dark:disabled:bg-white/5 dark:disabled:text-zinc-500
    sm:appearance-none sm:rounded-none sm:border-none sm:px-2 sm:py-1 sm:pr-2 sm:bg-transparent sm:font-normal sm:bg-none
  `}
            style={{
              backgroundImage:
                'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 20 20\' fill=\'%239CA3AF\'%3E%3Cpath d=\'M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.17l3.71-2.94a.75.75 0 1 1 .92 1.18l-4.17 3.31a.75.75 0 0 1-.92 0L5.21 8.41a.75.75 0 0 1 .02-1.2z\'/%3E%3C/svg%3E")',
            }}
          >
            <option value="all">전체</option>
            <option value="cloud">클라우드</option>
            <option value="local">로컬</option>
          </select>

        )}
      </div>

      {savedRaids.length > 0 ? (
        <div className="flex-1 basis-[0px] min-h-0 rounded-lg border border-zinc-950/10 bg-white overflow-hidden flex flex-col dark:border-white/10 dark:bg-zinc-900">
          {/* 검색 */}
          <div className="flex items-center gap-2 p-2 border-b border-zinc-950/10 dark:border-white/10">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="검색 (직업/레이드명/메모 등)"
              className="input-sm flex-1 dark:bg-zinc-800 dark:text-zinc-100 dark:placeholder:text-zinc-400 dark:border-white/10"
            />
            {selectedIds.length > 0 && (
              <button onClick={clearAll} className="btn btn-sm">
                선택 해제
              </button>
            )}
          </div>

          {/* 리스트 */}
          <div className="flex-1 overflow-auto basis-[0px]">
            <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
              {filtered.map(({ id, label, badges }) => {
                const selected = selectedIds.includes(id);
                return (
                  <li key={id}>
                    <label
                      className={[
                        "flex items-start gap-2 p-2 cursor-pointer",
                        selected ? "bg-indigo-50 dark:bg-indigo-500/10" : "hover:bg-zinc-50 dark:hover:bg-white/5",
                      ].join(" ")}
                    >
                      <input
                        type="checkbox"
                        className="shrink-0 mt-0.5 size-4 rounded border-zinc-950/10 text-indigo-600 focus:ring-indigo-500 dark:border-white/10 dark:bg-zinc-800 dark:text-indigo-400 dark:focus:ring-indigo-400"
                        checked={selected}
                        onChange={() => toggle(id)}
                        aria-checked={selected}
                      />
                      <span className="text-sm text-zinc-800 dark:text-zinc-200 whitespace-normal break-words leading-5">
                        {label}
                        {Array.isArray(badges) && badges.length > 0 && (
                          <span className="ml-2 inline-flex flex-wrap gap-1">
                            {badges.map((b, i) => (
                              <span
                                key={i}
                                className={[
                                  "inline-flex items-center rounded-full border px-1.5 py-0 text-[11px]",
                                  b.includes("클라우드")
                                    ? "bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-400/10 dark:text-sky-300 dark:border-sky-400/20"
                                    : "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-400/10 dark:text-emerald-300 dark:border-emerald-400/20",
                                ].join(" ")}
                              >
                                {b}
                              </span>
                            ))}
                          </span>
                        )}
                      </span>
                    </label>
                  </li>
                );
              })}
              {filtered.length === 0 && (
                <li className="p-4 text-sm text-zinc-500 dark:text-zinc-400">검색 결과가 없습니다.</li>
              )}
            </ul>
          </div>

          {/* 하단 버튼 */}
          <div className="flex gap-2 p-2 border-t border-zinc-950/10 dark:border-white/10">
            <button
              onClick={onLoadRaid}
              disabled={selectedIds.length !== 1}
              className="btn btn-sm flex-1"
            >
              선택 기록 보기
            </button>
            <button
              onClick={onAverage}
              disabled={!canAverage}
              className="btn btn-sm flex-1"
              title={
                selectedIds.length > 2
                  ? (canAverage ? '선택한 항목들의 평균을 표시합니다' : '같은 직업·같은 레이드만 평균을 낼 수 있어요')
                  : '평균을 내려면 2개 이상 선택하세요'
              }
            >
              선택 항목 평균
            </button>
            <button
              onClick={onCompare}
              disabled={!canCompare}
              className="btn btn-sm flex-1"
              title={
                selectedIds.length === 2
                  ? (canCompare ? '선택한 2개를 비교합니다' : '같은 직업·같은 레이드여야 비교할 수 있어요')
                  : '비교하려면 2개를 선택하세요'
              }
            >
              선택 항목 비교
            </button>
          </div>
        </div>
      ) : (
        <div className="text-sm text-zinc-500 dark:text-zinc-400 flex-1 items-center justify-center basis-[0px] rounded-lg border border-zinc-950/10 bg-white overflow-hidden flex flex-col dark:border-white/10 dark:bg-zinc-900">
          저장된 전투 기록이 없습니다.
        </div>
      )}
    </div>
  );
}
